import { NoticeType } from '../types/common';
import type { Notice } from '../types/common';

export class NoticeManager {
    private container: HTMLElement | null;

    constructor(containerId: string = 'toast-container') {
        this.container = document.getElementById(containerId);
    }

    public initialize(): void {
        if (!window.notices || window.notices.length === 0) return;
        if (!this.container) return;

        window.notices.forEach((notice, index) => {
            setTimeout(() => {
                this.showNotice(notice);
            }, index * 200);
        });
    }

    public showNotice(notice: Notice): void {
        if (!this.container) return;

        const toast = document.createElement('div');
        const borderColor = this.getBorderColor(notice.type);
        const iconColor = this.getIconColor(notice.type);

        toast.className = `bg-white rounded-md shadow-lg py-4 pl-4 pr-10 flex items-start gap-3 transform transition-all duration-300 ease-out -translate-x-full opacity-0 relative min-w-[300px] max-w-[400px] border-l-4 ${borderColor}`;

        const iconWrapper = document.createElement('div');
        iconWrapper.className = `w-5 h-5 shrink-0 mt-0.5 ${iconColor}`;
        iconWrapper.innerHTML = this.getNoticeIcon(notice.type);

        const messageDiv = document.createElement('div');
        messageDiv.className =
            'flex-1 text-sm text-gray-700 leading-snug pt-0.5';
        messageDiv.textContent = notice.content;

        const closeButton = document.createElement('button');
        closeButton.className =
            'absolute top-3 right-3 w-5 h-5 p-1 flex items-center justify-center rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all duration-200 cursor-pointer';
        closeButton.type = 'button';
        closeButton.title = 'Close';
        closeButton.innerHTML = `
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
        `;

        toast.appendChild(iconWrapper);
        toast.appendChild(messageDiv);
        toast.appendChild(closeButton);

        this.container.insertBefore(toast, this.container.firstChild);

        // Animate in
        setTimeout(() => {
            toast.classList.remove('-translate-x-full', 'opacity-0');
            toast.classList.add('translate-x-0', 'opacity-100');
        }, 100);

        // Dismiss functionality
        closeButton.addEventListener('click', () => {
            this.dismissNotice(toast);
        });

        // Auto-dismiss after 5 seconds
        setTimeout(() => {
            if (toast.parentNode) {
                this.dismissNotice(toast);
            }
        }, 5000);
    }

    private dismissNotice(toast: HTMLElement): void {
        toast.classList.remove('translate-x-0', 'opacity-100');
        toast.classList.add('-translate-x-full', 'opacity-0');
        setTimeout(() => {
            if (toast.parentNode) {
                toast.parentNode.removeChild(toast);
            }
        }, 300);
    }

    private getBorderColor(type: NoticeType): string {
        switch (type) {
            case NoticeType.Error:
                return 'border-red-500';
            case NoticeType.Warning:
                return 'border-yellow-500';
            case NoticeType.Success:
                return 'border-green-500';
            default:
                return 'border-blue-500';
        }
    }

    private getIconColor(type: NoticeType): string {
        switch (type) {
            case NoticeType.Error:
                return 'text-red-500';
            case NoticeType.Warning:
                return 'text-yellow-500';
            case NoticeType.Success:
                return 'text-green-500';
            default:
                return 'text-blue-500';
        }
    }

    private getNoticeIcon(type: NoticeType): string {
        switch (type) {
            case NoticeType.Error:
                return `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="15" y1="9" x2="9" y2="15"></line>
                    <line x1="9" y1="9" x2="15" y2="15"></line>
                </svg>`;
            case NoticeType.Warning:
                return `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"></path>
                    <path d="M12 9v4"></path>
                    <path d="m12 17 .01 0"></path>
                </svg>`;
            case NoticeType.Success:
                return `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22,4 12,14.01 9,11.01"></polyline>
                </svg>`;
            default:
                return `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <path d="M12 16v-4"></path>
                    <path d="m12 8 .01 0"></path>
                </svg>`;
        }
    }
}
